import React from "react";
import path from "ramda/src/path";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { useBlockedWords } from "./useBlockedWords";
import { CLIENTS_EMAIL_ADDRESS } from "../../constants/general";
import useAuth from "../../routes/login/AuthContext";
import { useBlockedWordsUploadHandler } from "./useBlockedWordsUploadHandler";
import { UploadButton } from "../UploadButton";
import { useNetwork } from "../../hooks/useNetwork";
import { Grid } from "../../UI/Grid";
import { Typography } from "../../UI/Typography";
import { HelpTooltip } from "../HelpTooltip";
import { Skeleton } from "../../UI/Skeleton";
import { Accordion } from "../../UI/Accordion";
import { AccordionSummary } from "../../UI/AccordionSummary";
import { AccordionDetails } from "../../UI/AccordionDetails";
import { List } from "../../UI/List";
import { ListItem } from "../../UI/ListItem";
import { ListItemText } from "../../UI/ListItemText";
import { AccordionActions } from "../../UI/AccordionActions";
import { COLOR_PICKLE_BLUEWOOD, COLOR_WHITE } from "../../constants/styles";
import { RequestChangeButton } from "../RequestChangeButton";
import { UploadBlockedWordsConfirmModal } from "../UploadBlockedWordsConfirmModal";

const TOOLTIP_HELP_TITLE = "Blocked Words";
const TOOLTIP_HELP_CONTENT =
  "This is a list of words and phrases that cannot be posted by a user in RoomSync. RoomSync actively filters content that residents submit as part of their lifestyle questions and within your network's 'Community Posts' area.";

const listItemPrimaryTypographyProps = {
  align: "center" as any,
};

export const BlockedWords = () => {
  const { isSuperAdmin, isSuperAdminWrite } = useAuth();
  const { selectedNetwork } = useNetwork();
  const [isExpanded, setIsExpanded] = React.useState(false);
  const { isLoading, blockedWords, getBlockedWords } = useBlockedWords();
  const { getInputProps, getRootProps, uploadValues, acceptedFiles } = useBlockedWordsUploadHandler();

  const toogleAccardion = (_: React.ChangeEvent<{}>, expanded: boolean) => setIsExpanded(expanded);

  return (
    <Grid container spacing={2}>
      {isLoading && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Skeleton variant="rectangular" height={50} />
            </Grid>
          </Grid>
        </Grid>
      )}
      {!isLoading && (
        <>
          <Grid item xs={12}>
            <Accordion expanded={isExpanded} onChange={toogleAccardion}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon htmlColor={COLOR_WHITE} />}
                sx={{ bgcolor: COLOR_PICKLE_BLUEWOOD }}
              >
                <HelpTooltip title={TOOLTIP_HELP_TITLE} text={TOOLTIP_HELP_CONTENT}>
                  <Typography color={COLOR_WHITE}>Blocked words</Typography>
                </HelpTooltip>
              </AccordionSummary>
              <AccordionDetails>
                <List dense sx={{ maxHeight: 400, overflow: "scroll" }}>
                  {blockedWords.map((word) => (
                    <React.Fragment key={word.id}>
                      <ListItem>
                        <ListItemText primaryTypographyProps={listItemPrimaryTypographyProps} primary={word.pattern} />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </AccordionDetails>
              <AccordionActions>
                <RequestChangeButton
                  url={`mailto:${CLIENTS_EMAIL_ADDRESS}?subject=RoomSync Blocked Words change request for ${selectedNetwork.l1Name}`}
                />
              </AccordionActions>
            </Accordion>
          </Grid>
          {isSuperAdmin() && isSuperAdminWrite() && (
            <Grid item xs={12}>
              <UploadButton
                inputProps={getInputProps()}
                title="Replace blocked words"
                inputContainerProps={getRootProps({ className: "dropzone" })}
              >
                Upload CSV file here.
              </UploadButton>
              <UploadBlockedWordsConfirmModal onSuccess={getBlockedWords} file={path([0], acceptedFiles)}>
                <List dense sx={{ width: "100%", maxHeight: 350, overflow: "auto" }}>
                  {uploadValues.map((value) => (
                    <React.Fragment key={value}>
                      <ListItem>
                        <ListItemText primaryTypographyProps={listItemPrimaryTypographyProps} primary={value} />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </UploadBlockedWordsConfirmModal>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
